/* eslint-disable no-unused-vars */
import { TagHandlerBase } from './tagHandlerBase';
import store from '@/store';
import { isScaffoldPlaced } from '@/scripts/js/forgeModels';
const THREE = window.THREE;

/**
 * Handler for scaffold, permit, and container tags
 */
export class ScaffoldHandler extends TagHandlerBase {
    /**
     * Check if tag type should be processed
     * @param {string} type - Tag type
     * @param {Object} displaySettings - Display settings
     * @returns {boolean} Should process scaffold
     */
    shouldProcess(type, displaySettings) {
        return (displaySettings.showScaffolds || displaySettings.showPermits || displaySettings.showDeckManagment) && 
               (type === 'scaffold' || type === 'permit' || type === 'container');
    }
    
    /**
     * Collect scaffold data
     * @param {Object} settings - Display settings
     * @param {Object} viewerData - Viewer position data
     * @returns {Object} Collected scaffold data
     */
    collectData(settings, viewerData) {
        const { isGlobalSearchFilter, isGlobalSearchTabShown, displaySettings } = settings;
        const data = {};
        
        // Clear any existing cluster text meshes
        this.clearClusterTextMeshes();
        
        // Get scaffold type from extension
        const type = this.extension.currentTagType || 'scaffold';
        
        // Skip if not applicable
        if (!this.shouldProcess(type, displaySettings)) return data;
        
        let scaffolds = this.isViewer2d() 
            ? store.state.forgeService.getScaffoldsInLevel(store.state.selectedLevel) 
            : store.state.scaffolds;
            
        scaffolds = store.state.forgeService.updateListWithEditingTag(scaffolds);

        if (isGlobalSearchFilter) {
            let isolatedTags = store.getters['GET_ISOLATED_IDS'];    
            if (isolatedTags.size > 0) {
                scaffolds = scaffolds.filter(t => isolatedTags.has(t.id));
            }
        }
        
        // Filter to only include the specific type and apply display filters before clustering
        scaffolds = scaffolds.filter(scaffold => {
            // Keep only matching type
            if (scaffold.type !== type) return false;

            // An unplaced tag has no marker to draw. A register-created one has never been
            // placed and a cleared one has just been unplaced; both sit at the origin, so
            // without this they render as a stray icon at the model origin (AVA-492).
            if (!isScaffoldPlaced(scaffold)) return false;
            
            // Modified to comment out global search filtering
            // Check display settings only, without global search filtering
            if (scaffold.isScaffold()) {
                if (!displaySettings.showScaffolds) return false;
                /* Comment out global search filtering
                if (isGlobalSearchTabShown && isGlobalSearchFilter && !store.state.filteredScaffolds.includes(scaffold)) return false;
                */
            } else if (scaffold.isPermit()) {
                if (!displaySettings.showPermits) return false;
                /* Comment out global search filtering
                if (isGlobalSearchTabShown && isGlobalSearchFilter && !store.state.filteredPermits.includes(scaffold)) return false;
                */
            } else if (scaffold.isContainer()) {
                if (!displaySettings.showDeckManagment) return false;
                /* Comment out global search filtering
                if (isGlobalSearchTabShown && isGlobalSearchFilter && !store.state.filteredContainers.includes(scaffold)) return false;
                */
            }
            
            return true;
        });
        
        // Only apply clustering to filtered scaffolds in 2D view
        if (this.isViewer2d() && scaffolds.length > 0) {
            // Apply clustering logic
            const { items, clusters } = this.getTagsClustersAndItems2D(scaffolds, viewerData.position);
            
            // Process clusters
            this.processClusters(clusters, data, 'scaffold-cluster');
            
            // Update to use filtered items
            scaffolds = items;
        }
        
        scaffolds.forEach(scaffold => {
            // Get point in viewer space
            let point = this.getPointForScaffold(scaffold);
            if (!point) return;
            
            // Get pin color
            let color = scaffold.getPinVector();
            
            // Create point cloud data
            const pcData = this.createScaffoldData(scaffold, point, color);
            
            // Add to default category (0)
            data[0] ? data[0].push(pcData) : data[0] = [pcData];
            
            // Draw tag area
            this.drawTagArea(scaffold);
        });
        
        return data;
    }
    
    /**
     * Check if scaffold should be skipped
     * @param {Object} scaffold - Scaffold object
     * @param {string} type - Tag type
     * @param {boolean} isGlobalSearchTabShown - Global search tab shown flag
     * @param {boolean} isGlobalSearchFilter - Global search filter flag
     * @param {Object} displaySettings - Display settings
     * @returns {boolean} Should skip scaffold
     */
    shouldSkipScaffold(scaffold, type, isGlobalSearchTabShown, isGlobalSearchFilter, displaySettings) {
        if (scaffold.isScaffold()) {
            if (!displaySettings.showScaffolds) return true;
            if (isGlobalSearchTabShown && isGlobalSearchFilter && !store.state.filteredScaffolds.includes(scaffold)) return true;
        } else if (scaffold.isPermit()) {
            if (!displaySettings.showPermits) return true;
            if (isGlobalSearchTabShown && isGlobalSearchFilter && !store.state.filteredPermits.includes(scaffold)) return true;
        } else if (scaffold.isContainer()) {
            if (!displaySettings.showDeckManagment) return true;
            if (isGlobalSearchTabShown && isGlobalSearchFilter && !store.state.filteredContainers.includes(scaffold)) return true;
        }
        return false;
    }
    
    /**
     * Get point in viewer space for scaffold
     * @param {Object} scaffold - Scaffold object
     * @returns {Object} Point in viewer space
     */
    getPointForScaffold(scaffold) {
        // The marker sits on the scaffold's exact stored position — the point the user
        // picked — so it agrees with the box mesh, the zoom highlight and the crop box.
        // Always return a NEW vector: getPointInView() hands back the tag's own position
        // by reference when there is no model and no unitScale, and writing to it would
        // corrupt the stored position on every icon refresh.
        if (this.isViewer2d()) {
            const point2d = store.state.selectedLevel.mappingViewersPointsInfo._3dTo2d(scaffold.position);

            return point2d ? new THREE.Vector3(point2d.x, point2d.y, .1) : null;
        }

        const point = scaffold.getPointInView();

        return point ? new THREE.Vector3(point.x, point.y, point.z) : null;
    }
    
    /**
     * Create scaffold data for point cloud
     * @param {Object} scaffold - Scaffold object
     * @param {Object} point - Point in viewer space
     * @param {Object} color - Color object
     * @returns {Object} Point cloud data
     */
    createScaffoldData(scaffold, point, color) {
        const _this = this;
        return {
            name: scaffold.name,
            thumbnailURL: scaffold.thumbnailUrl,
            object: scaffold,
            pinColor: color,
            point: point,
            onClick: (event) => {
                store.dispatch('FETCH_SCAFFOLD_TAG_BY_ID', scaffold.id).then(tag => {
                    store.state.forgeService.onClickOn3dIcon(tag, event, _this.extension.viewType);
                });
            }
        };
    }
}
